import React, { useContext } from 'react';
import {
    LEGAL_SOURCE_TRUE_ID,
    MESSAGE_NOT_AVAILABLE_IN_YEAR
} from '../constants';
import { debugRun } from '../debug/debug.utils';
import { AppContext } from '../state/context';
import {
    AppState,
    CountryProfileFilters,
    CountryProfileResult,
    Taxonomies
} from '../types';
import {
    getCountryTaxonomyListByRegionIdList,
    getLatestYear,
    getFullYearFromDatetime,
    getLargeDate,
} from '../utils';

const initActions = (
    state: AppState,
    changeState: React.Dispatch<React.SetStateAction<AppState>>
) => {
    const setState = (stateUpdates: Partial<AppState>) => {
        changeState({
            ...state,
            ...stateUpdates
        });
    };

    const getPage = (pageName: string) => {
        for (const page of state.pages) {
            if (page.name === pageName) {
                return page;
            }
        }

        return null;
    };

    const getCountryProfile = (
        countryIdFilter: CountryProfileFilters['countryIds'][0],
        localLevelFilter: CountryProfileFilters['localLevel'] = false,
        datetimeFilter: CountryProfileFilters['datetime'] = null,
    ) => {

        const resultList = getCountryProfileResultList({
            countryIds: [countryIdFilter],
            localLevel: localLevelFilter,
            datetime: datetimeFilter,
        });

        const countryProfile = resultList[0]?.countryProfile || null;

        debugRun(({ warn }) => {
            if (resultList.length > 1) {
                warn(`WARNING: getCountryProfile returned more than one result for countryId ${countryIdFilter}`);
            }
        });

        return countryProfile;
    };

    const getCountryProfileResultList = (
        filters: Partial<CountryProfileFilters>
    ): CountryProfileResult[] => {
        const {
            localLevel = false,
            countryIds,
            datetime,
            regionIds,
            electoralSystemIds,
            typeOfQuotaIds,
            legalSourcesIds,
        } = filters;

        const trustCountryIds = (() => {
            const countryIdListFromCountryIdsFilter = countryIds || [];
            const countryIdListFromRegionIdsFilter = getCountryTaxonomyListByRegionIdList(
                state.taxonomies.countries,
                regionIds || []
            )
                .filter(taxonomy => (
                    localLevel
                        ? taxonomy.hasLocalLevelData
                        : taxonomy.hasNationalLevelData
                ))
                .map(taxonomy => taxonomy.id);
            const countryIdListFromFilters = [
                ...countryIdListFromCountryIdsFilter,
                ...countryIdListFromRegionIdsFilter
            ]
                .filter((countryId, i, countryIdList) => (
                    countryIdList.indexOf(countryId) === i
                ));

            if (countryIds?.length || regionIds?.length) {
                return countryIdListFromFilters;
            }

            return state.taxonomies.countries
                .filter(countryTaxonomy => (
                    localLevel
                        ? countryTaxonomy.hasLocalLevelData
                        : countryTaxonomy.hasNationalLevelData
                ))
                .map(taxonomy => taxonomy.id);
        })();

        const validCountryProfileList = state.countryProfiles.filter(countryProfile => (
            countryProfile.localLevel === localLevel
        ));
        const latestYear = datetime ? null : getLatestYear(validCountryProfileList);

        debugRun(({ log }) => {
            const messages = [
                `Get profiles (${trustCountryIds.length})`,
            ];

            if (datetime) {
                messages.push(`Using datetime: ${getLargeDate(datetime)}`);
            }
            else {
                if (latestYear) {
                    messages.push(`Using LATEST YEAR: ${latestYear}`);
                }
                else {
                    messages.push(`No LATEST YEAR available in dataset`);
                }
            }

            messages.push(localLevel ? 'Local level' : '');

            log(messages.filter(Boolean).join(' | '));
        });

        let datedCountryProfileList = validCountryProfileList.filter(countryProfile => (
            datetime
                ? countryProfile.datetime === datetime
                : !latestYear || getFullYearFromDatetime(countryProfile.datetime) === latestYear
        ));

        if (!datetime && latestYear) {
            // Select the current profile before applying filters to its attributes.
            datedCountryProfileList = datedCountryProfileList.reduce<typeof datedCountryProfileList>((profiles, profile) => {
                const index = profiles.findIndex(item => item.countryId === profile.countryId);

                if (index === -1) {
                    profiles.push(profile);
                }
                else if (profile.datetime > profiles[index].datetime) {
                    profiles[index] = profile;
                }

                return profiles;
            }, []);
        }

        const filteredCountryProfileList = datedCountryProfileList.filter(countryProfile => {
            let isValid = trustCountryIds.indexOf(countryProfile.countryId) !== -1;

            if (isValid && electoralSystemIds && electoralSystemIds.length) {
                isValid = electoralSystemIds.indexOf(countryProfile.electoralSystemTypeId) !== -1;
            }

            if (isValid && typeOfQuotaIds && typeOfQuotaIds.length) {
                isValid = false;

                for (const typeOfQuotaId of typeOfQuotaIds) {
                    if (countryProfile.typeOfQuotaIds.indexOf(typeOfQuotaId) !== -1) {
                        isValid = true;
                        break;
                    }
                }
            }

            if (isValid && legalSourcesIds && legalSourcesIds.length) {
                isValid = false;

                for (const id of legalSourcesIds) {
                    if ((id === 1) && (countryProfile.constitutionProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID)) isValid = true;
                    if ((id === 2) && (countryProfile.electoralProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID)) isValid = true;
                    if ((id === 3) && (countryProfile.financingProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID)) isValid = true;
                    if ((id === 4) && (countryProfile.parityProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID)) isValid = true;
                    if ((id === 5) && (countryProfile.partyProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID)) isValid = true;
                    if ((id === 6) && (countryProfile.politicalProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID)) isValid = true;
                    if ((id === 7) && (countryProfile.localProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID)) isValid = true;
                }
            }

            return isValid;
        });

        return trustCountryIds
            .map(countryId => {
                const countryProfile = filteredCountryProfileList
                    .find(profile => profile.countryId === countryId) || null;
                
                return {
                    countryId,
                    countryProfile,
                    message: countryProfile ? null : MESSAGE_NOT_AVAILABLE_IN_YEAR
                };
            });
    };

    const getTaxonomies = (
        filters?: CountryProfileFilters
    ): Taxonomies => (
        filters ? {
            dates: filters.localLevel
                ? state.taxonomies.dates.filter(dateTaxonomy => (
                    getFullYearFromDatetime(dateTaxonomy.datetime) > 2022
                ))
                : state.taxonomies.dates,
            countries: filters.localLevel
                ? state.taxonomies.countries.filter(countryTaxonomy => (
                    countryTaxonomy.hasLocalLevelData
                ))
                : state.taxonomies.countries.filter(countryTaxonomy => (
                    countryTaxonomy.hasNationalLevelData
                )),
            typesOfQuota: state.taxonomies.typesOfQuota,
            sanctions: state.taxonomies.sanctions,
            electoralSystemTypes: state.taxonomies.electoralSystemTypes,
            rankingPlacement: state.taxonomies.rankingPlacement,
            legalSources: filters.localLevel
                ? state.taxonomies.legalSources
                : state.taxonomies.legalSources.filter(legalSource => (
                    legalSource.id !== 7
                )),
            regions: state.taxonomies.regions,
            quotaForInputOnly: state.taxonomies.quotaForInputOnly,
            parliamentStructure: state.taxonomies.parliamentStructure,
            quotaTargetMet: state.taxonomies.quotaTargetMet,
            provisionsOnGenderQuotas_yesNo: state.taxonomies.provisionsOnGenderQuotas_yesNo,
        } : state.taxonomies
    );

    return {
        setState,
        getPage,
        getCountryProfile,
        getCountryProfileResultList,
        getTaxonomies,
    };
};

export const useAppState = () => {
    const {state, changeState} = useContext(AppContext);
    const actions = initActions(state, changeState);

    return [state, actions] as [AppState, typeof actions];
};
